/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from "react";
import { Modal as Mmodal } from "react-responsive-modal";
import "../modal/modal.css";
import Button from "../form/button/button";
import axiosInstance from "../../config/axios";
import { showToast } from "../../utils/utils";

const MOTIFS_AV = [
  "annulation totale",
  "annulation partielle",
  "correction administrative",
  "correction de montant",
  "correction de TVA",
  "geste commercial",
  "régularisation",
];

const MOTIFS_AVJ = [
  "annulation totale",
  "annulation partielle",
  "correction administrative",
  "correction de montant",
  "correction de TVA",
  "régularisation",
  "autre motif à préciser",
];

function resolveAvoirKind(factureType: string) {
  if (["commission", "ft", "fac", "annulation"].includes(factureType)) return "AV";
  if (["rémunération", "fvj"].includes(factureType)) return "AVJ";
  return "AV";
}

export default function AvoirModal(props: {
  show: boolean;
  facture: any;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [montantType, setMontantType] = useState<"total" | "partial">("total");
  const [priceHT, setPriceHT] = useState("");
  const [motif, setMotif] = useState("");
  const [motifAutre, setMotifAutre] = useState("");
  const [remainingHT, setRemainingHT] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const avoirKind = resolveAvoirKind(props.facture?.type);
  const motifs = avoirKind === "AVJ" ? MOTIFS_AVJ : MOTIFS_AV;

  useEffect(() => {
    setOpen(props.show);
    if (props.show && props.facture) {
      setMontantType("total");
      setPriceHT("");
      setMotif("");
      setMotifAutre("");
      loadRemaining();
    }
  }, [props.show, props.facture?._id]);

  async function loadRemaining() {
    if (!props.facture?._id) return;
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("avoirs/facture/" + props.facture._id);
      const credited = (data || []).reduce((sum: number, a: any) => sum + +a.priceHT, 0);
      setRemainingHT(+(+props.facture.priceHT - credited).toFixed(2));
    } catch (e) {
      setRemainingHT(+props.facture?.priceHT || 0);
    }
    setLoading(false);
  }

  async function onConfirm() {
    const finalMotif =
      motif === "autre motif à préciser" ? motifAutre.trim() : motif.trim();
    if (!finalMotif) return showToast("Le motif est obligatoire");
    if (montantType === "partial") {
      const ht = +priceHT;
      if (isNaN(ht) || ht <= 0) return showToast("Montant HT invalide");
      if (ht > remainingHT) return showToast(`Le montant ne peut pas dépasser ${remainingHT.toFixed(2)} € HT`);
    }
    if (remainingHT <= 0) return showToast("Cette facture a déjà été intégralement créditée");

    setSubmitting(true);
    try {
      await axiosInstance.post("avoirs", {
        idFacture: props.facture._id,
        montantType,
        priceHT: montantType === "partial" ? priceHT : undefined,
        motif: finalMotif,
      });
      showToast(`Avoir ${avoirKind} généré avec succès`, "success");
      props.onSuccess();
      props.onClose();
    } catch (e: any) {
      showToast(e?.response?.data?.message || "Erreur lors de la génération de l'avoir");
    }
    setSubmitting(false);
  }

  return (
    <Mmodal
      closeIcon={<img width={28} src="/images/close.svg" alt="" />}
      styles={{ modal: { borderRadius: 8, minWidth: "35vw", maxWidth: 480, padding: 25 } }}
      open={open}
      onClose={() => {
        setOpen(false);
        props.onClose();
      }}
      center
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <h4 style={{ marginTop: 10 }}>Générer un avoir {avoirKind}</h4>
        <p style={{ fontSize: 14, color: "#666" }}>
          Facture <strong>{props.facture?.numFacture}</strong>
          {loading ? " — calcul..." : ` — restant créditable : ${remainingHT.toFixed(2)} € HT`}
        </p>

        <div>
          <label style={{ fontWeight: 600, fontSize: 14, display: "block", marginBottom: 8 }}>Montant</label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, cursor: "pointer" }}>
            <input
              type="radio"
              checked={montantType === "total"}
              onChange={() => setMontantType("total")}
            />
            Avoir total ({remainingHT.toFixed(2)} € HT)
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input
              type="radio"
              checked={montantType === "partial"}
              onChange={() => setMontantType("partial")}
            />
            Avoir partiel
          </label>
        </div>

        {montantType === "partial" && (
          <div>
            <label style={{ fontWeight: 600, fontSize: 14 }}>Montant HT (€)</label>
            <input
              type="number"
              min={0}
              max={remainingHT}
              step="0.01"
              value={priceHT}
              onChange={(e) => setPriceHT(e.target.value)}
              style={{ width: "100%", padding: 10, marginTop: 6, borderRadius: 6, border: "1px solid #ddd" }}
              placeholder={`Max ${remainingHT.toFixed(2)}`}
            />
          </div>
        )}

        <div>
          <label style={{ fontWeight: 600, fontSize: 14 }}>Motif *</label>
          <select
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            style={{ width: "100%", padding: 10, marginTop: 6, borderRadius: 6, border: "1px solid #ddd" }}
          >
            <option value="">Sélectionner un motif</option>
            {motifs.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          {motif === "autre motif à préciser" && (
            <textarea
              value={motifAutre}
              onChange={(e) => setMotifAutre(e.target.value)}
              rows={2}
              style={{
                width: "100%",
                padding: 10,
                marginTop: 8,
                borderRadius: 6,
                border: "1px solid #ddd",
                resize: "vertical",
              }}
              placeholder="Précisez le motif"
            />
          )}
        </div>

        <Button
          className="btn-primary mb-2"
          onClick={onConfirm}
          title={submitting ? "Génération..." : `Générer l'avoir ${avoirKind}`}
          disabled={submitting || remainingHT <= 0}
        />
        <Button className="btn-secondary" onClick={props.onClose} title="Annuler" />
      </div>
    </Mmodal>
  );
}
